// Bausteine für Seiten nach dem Prinzip „Lesemodus + Aktionsleiste + Bestätigungsdialog“.
import type { LucideIcon } from 'lucide-react'
import { Inbox } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export function PageHeader({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  /** Rechts neben dem Titel, z. B. eine Aktionsleiste */
  children?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 max-w-prose text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  )
}

/** Klarer Leerzustand: sagt, was hier erscheint und warum es gerade leer ist. */
export function EmptyState({
  title,
  body,
  icon: Icon = Inbox,
  children,
}: {
  title: string
  body?: string
  icon?: LucideIcon
  children?: ReactNode
}) {
  return (
    <div
      role="status"
      className="flex flex-col items-center gap-3 rounded-lg border border-dashed bg-card px-6 py-14 text-center"
    >
      <Icon className="size-10 text-muted-foreground" aria-hidden="true" />
      <h2 className="text-lg font-medium">{title}</h2>
      {body && <p className="max-w-prose text-muted-foreground">{body}</p>}
      {children}
    </div>
  )
}

/** Lesemodus: Beschriftung/Wert-Paare als Definitionsliste. */
export function ReadOnlyFields({
  items,
  className,
}: {
  items: { label: string; value: ReactNode }[]
  className?: string
}) {
  return (
    <dl
      className={cn('grid gap-x-8 gap-y-4 rounded-lg border bg-card p-5 sm:grid-cols-2', className)}
    >
      {items.map((item) => (
        <div key={item.label}>
          <dt className="text-sm text-muted-foreground">{item.label}</dt>
          <dd className="mt-0.5 font-medium break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Aktionsleiste: alle Änderungen laufen über Schaltflächen hier (nie als Inline-Bearbeitung im Lesemodus).
 * Ohne Aktionen wird nichts gerendert.
 */
export function ActionBar({
  label,
  children,
  emptyHint,
}: {
  label: string
  children?: ReactNode
  /** Text, wenn keine Aktionen möglich sind */
  emptyHint?: string
}) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) : Boolean(children)
  if (!hasChildren) {
    return emptyHint ? <p className="text-sm text-muted-foreground">{emptyHint}</p> : null
  }
  return (
    <div role="toolbar" aria-label={label} className="flex flex-wrap items-center gap-3">
      {children}
    </div>
  )
}
